import React from "react"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

interface EmailBandDialogProps {
  bandName: string
  bandEmail: string
  onClose: () => void
}

// Craigslist-style email flow: nothing auto-launches on open — just shows
// the address to copy, plus explicit opt-in buttons (Gmail, Outlook, local
// mail app) so the fan picks how to send, instead of the previous version
// which hardcoded a Gmail redirect for everyone.
export const EmailBandDialog: React.FC<EmailBandDialogProps> = ({
  bandName,
  bandEmail,
  onClose,
}) => {
  const subject = `Hey ${bandName}! -- (via bandNada)`
  const body = `Hey ${bandName},\n\n\n\n(powered by bandNada)`

  const gmailUrl =
    "https://mail.google.com/mail/u/0/?" +
    new URLSearchParams({
      view: "cm",
      fs: "1",
      tf: "1",
      to: bandEmail,
      su: subject,
      body,
    }).toString()

  const outlookUrl =
    "https://outlook.live.com/mail/0/deeplink/compose?" +
    new URLSearchParams({
      to: bandEmail,
      subject,
      body,
    }).toString()

  const mailtoUrl =
    `mailto:${bandEmail}?` +
    new URLSearchParams({
      subject,
      body,
    }).toString()

  return (
    <Dialog
      isOpen
      onDismiss={onClose}
      aria-label={`Email ${bandName}`}
      style={{ maxWidth: 420, width: "90%" }}
    >
      <div className="flex justify-end mb-2">
        <button
          className="close-button focus:outline-none text-lg"
          onClick={onClose}
          type="button"
        >
          <span aria-hidden>×</span>
        </button>
      </div>

      <div className="mb-4">
        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
          Email Address
        </div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={bandEmail}
            onFocus={(e) => e.target.select()}
            className="flex-1 border border-gray-300 rounded px-2 py-2 text-sm bg-gray-50"
          />
          <CopyToClipboard
            text={bandEmail}
            onCopy={() =>
              toast.success(`Copied ${bandEmail} to your clipboard!`, {
                position: "top-center",
                autoClose: 2000,
                hideProgressBar: true,
              })
            }
          >
            <button
              type="button"
              className="px-3 py-2 rounded border-2 border-green-700 text-green-700 font-bold text-sm focus:outline-none whitespace-nowrap"
            >
              Copy
            </button>
          </CopyToClipboard>
        </div>
      </div>

      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
        Or open a new email in
      </div>
      <div className="flex gap-2">
        <a
          href={gmailUrl}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-3 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
        >
          Gmail
        </a>
        <a
          href={outlookUrl}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-3 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
        >
          Outlook
        </a>
        <a
          href={mailtoUrl}
          className="flex-1 text-center px-3 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
        >
          Mail App
        </a>
      </div>
    </Dialog>
  )
}

export default EmailBandDialog
